<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 id="draft-title" class="mb-0">
                <i :class="typeIconClass + ' mr-2'" />Draft {{ typeLabel }} Emas
            </h5>
            <hr />
            <b-row>
                <b-col id="batch-details" md="6">
                    No. Batch <strong>{{ data.batch_number || "-" }}</strong><br />
                    Tipe <strong>{{ typeLabel }}</strong><br />
                    Status <strong><b-badge :variant="getStatusBadge(data.status)[0]">{{ getStatusBadge(data.status)[1]
                    }}</b-badge></strong><br />
                    Gudang <strong>{{ data.warehouse ? data.warehouse : "-" }}</strong><br />
                    Dibuat oleh <strong>{{ data.created_by ? data.created_by : "-" }}</strong><br />
                      {{ totalRows }} barang<br />
                    Total Berat Bersih: <strong>{{ data.net_weight ? data.net_weight.toFixed(2) : "-" }}
                        gram</strong><br />
                </b-col>
                <b-col id="batch-actions" md="6" class="text-right">
                    <b-button id="cancel-batch-button" variant="danger" @click="promptCancelBatch">
                        <i class="fa fa-times mr-2" />Batalkan
                    </b-button>
                    <b-button id="process-batch-button" variant="success" class="ml-2" :disabled="!canProcess"
                        @click="openProcessModal">
                        <i :class="processIconClass + ' mr-2'" />{{ processButtonLabel }}
                    </b-button>
                </b-col>
            </b-row>
            <b-row class="mt-3" id="search-item-row">
                <b-col md="6" id="search-item-col">
                    <b-input-group id="search-item-input-group">
                        <b-input v-model="input.noFaktur" maxlength="100" style="max-width: 250px"
                            placeholder="Input No. Faktur..." @keydown.enter="findItem" id="search-item-input" />
                        <b-input-group-append id="search-item-input-group-append">
                            <b-button variant="dark" @click="findItem" id="search-item-button">Cari Barang</b-button>
                        </b-input-group-append>
                        <b-button class="ml-2" variant="primary" @click="openUploadModal">
                            <i class="fa fa-upload mr-2" />Upload Excel
                        </b-button>

                    </b-input-group>
                </b-col>
            </b-row>
            <b-table id="batch-items-table" bordered hover show-empty no-local-sorting :items="data.items"
                :busy="isBusy" :fields="fields" responsive class="my-3">
                <template v-slot:empty>
                    Batch masih kosong.
                </template>
                <template v-slot:table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle mr-2" />
                        <strong>Memuat...</strong>
                    </div>
                </template>
                <template v-slot:cell(item)="row">
                    {{ row.item.no_faktur_pgi }}
                    <div class="small text-muted">
                        {{ row.item.item_kind_name || "-" }} |
                        {{ row.item.brand_name || "-" }} <br />
                        {{ row.item.type_name || "-" }}
                    </div>
                </template>
                <template v-slot:cell(dry_weight)="row">
                    {{ row.item.dry_weight ? row.item.dry_weight.toFixed(2) : "-" }} gr
                </template>
                <template v-slot:cell(weight_reduction)="row">
                    {{ row.item.weight_reduction ? row.item.weight_reduction.toFixed(2) : "0" }} gr
                </template>
                <template v-slot:cell(purity)="row">
                    {{ row.item.purity || "-" }}
                </template>
                <template v-slot:cell(action)="row">
                    <b-button id="remove-item-button" size="sm" variant="danger" v-b-tooltip.hover.top title="Hapus"
                        @click="promptRemoveItem(row.item)">
                        <i class="fa fa-fw fa-times" />
                    </b-button>
                </template>
            </b-table>
            <PageSizer :totalRows="totalRows" :page="currentPage" :options="pageSizeOptions"
                @limit-change="onLimitChange" @page-change="onPageChange" />
        </b-card>

        <b-modal id="m-item-to-add" size="xl" title="Tambah Barang ke Batch" centered no-close-on-backdrop
            no-close-on-esc hide-footer>
            <ModalAddItem :key="noFakturPGI" :batchId="Number($route.params.id)" :noFaktur="noFakturPGI"
                :type="batchType" @item-added="onItemAdded" />
        </b-modal>

        <ModalExecuteProcess ref="modalExecuteProcess" :batchId="Number($route.params.id)" :type="batchType"
            @processed="onProcessed" />

        <ModalUploadExcel ref="modalUploadExcel" :batchId="Number($route.params.id)" @uploaded="onUploadComplete" />
    </div>
</template>

<script>
import ModalAddItem from "./ModalAddItem.vue";
import ModalExecuteProcess from "./ModalExecuteProcess.vue";
import ModalUploadExcel from "./ModalUploadExcel.vue";

export default {
    components: {
        ModalAddItem,
        ModalExecuteProcess,
        ModalUploadExcel
    },
    data() {
        return {
            initStatus: 0,
            isSubmitting: false,

            data: {
                items: [],
                type: 1
            },

            input: {
                noFaktur: ""
            },

            noFakturPGI: "",
            isBusy: true,
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageSizeOptions: [10, 20, 50],

            fields: [
                { key: "item", label: "No Faktur & Barang", sortable: false },
                { key: "dry_weight", label: "Berat Kotor", sortable: false },
                { key: "weight_reduction", label: "Potongan Berat", sortable: false },
                { key: "purity", label: "Kadar", sortable: false },
                { key: "action", label: "Aksi" }
            ]
        };
    },

    computed: {
        batchType() {
            return this.data.type || 1;
        },
        typeLabel() {
            return this.batchType === 1 ? "Lebur" : "Pemurnian";
        },
        typeIconClass() {
            return this.batchType === 1 ? "fa fa-fw fa-fire" : "fa fa-fw fa-flask";
        },
        processButtonLabel() {
            return this.batchType === 1 ? "Lebur" : "Murnikan";
        },
        processIconClass() {
            return this.batchType === 1 ? "fa fa-fire" : "fa fa-flask";
        },
        canProcess() {
            return this.data.items && this.data.items.length > 0 && this.data.status === 1;
        },
    },

    methods: {
        init() {
            this.initStatus = 0;
            this.getBatch(1)
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        getBatch(page) {
            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            this.isBusy = true;

            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage, true);

            return this.$axios
                .get("gold-batches/" + this.$route.params.id + param)
                .then(res => {
                    const resData = res.data.data;
                    this.data = resData;
                    this.totalRows = resData.total || 0;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                }).finally(() => {
                    this.isBusy = false;
                });
        },

        findItem() {
            if (this.$helper.isBlank(this.input.noFaktur)) {
                return this.$helper.toastError(this, "Mohon mengisi nomor faktur!");
            }

            this.noFakturPGI = this.input.noFaktur;
            this.$bvModal.show("m-item-to-add");
        },

        onItemAdded(toastMessage) {
            this.$bvModal.hide("m-item-to-add");
            this.$helper.toastSuccess(this, toastMessage);
            this.input.noFaktur = "";
            this.noFakturPGI = "";
            this.getBatch(this.currentPage);
        },

        promptRemoveItem(item) {
            let confirmed = confirm(`Hapus barang ${item.no_faktur_pgi} dari batch ini?`);
            if (!confirmed) return;

            this.$axios
                .delete(`gold-batches/${this.$route.params.id}/items/${item.id}`)
                .then(() => {
                    this.$helper.toastSuccess(this, "Barang berhasil dihapus dari batch!");
                    this.getBatch(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        promptCancelBatch() {
            let isConfirmed = confirm("Batalkan batch ini? Batch akan dihapus.");
            if (!isConfirmed) return;

            this.$axios
                .delete("gold-batches/" + this.$route.params.id)
                .then(() => {
                    this.$helper.toastSuccess(this, "Batch berhasil dibatalkan!");
                    this.$router.push({ path: "/emas/gold-batches" });
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        openProcessModal() {
            let modalId = this.batchType === 1 ? "m-execute-melt" : "m-execute-refine";
            this.$refs.modalExecuteProcess.$bvModal.show(modalId);
        },

        onProcessed(toastMessage) {
            this.$helper.toastSuccess(this, toastMessage);
            this.$router.push({ path: "/emas/gold-batches" });
        },

        openUploadModal() {
            this.$refs.modalUploadExcel.open();
        },

        onUploadComplete() {
            this.getBatch(this.currentPage);
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getBatch(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getBatch(this.currentPage);
        },

        getStatusBadge(status) {
            switch (status) {
                case 1:
                    return ["dark", "DRAFT"];
                case 2:
                    return ["success", this.batchType === 1 ? "DILEBUR" : "DIMURNIKAN"];
                default:
                    return ["light", "-"];
            }
        }
    },

    created() {
        this.init();
    }
};
</script>
